import { Button } from "components/DesignSystem/Button/Button";
import { Combobox } from "components/DesignSystem/Combobox/Combobox";
import Modal from "components/DesignSystem/Modal/Modal";
import { FormikForm } from "components/FormikForm/FormikForm";
import { WFormEmailPreview } from "components/WFormEmailPreview";
import { CURRENT_SEASON } from "constants/vendor";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useToast } from "hooks/useToast";
import { useBulkWformRequestMutation } from "store/apis/taskMerchants";
import { useGetTeamMembersQuery } from "store/apis/teamSetting";
import * as Yup from "yup";

type MerchantDetail = {
  email: string;
  merchant_id: string;
};

type BulkWFormRequestProps = {
  isOpen: boolean;
  close: () => void;
  groupId: string;
  merchantDetails: MerchantDetail[];
  updateMerchant: () => void;
  season: string;
  handleSuccess: () => void;
};

const validationSchema = Yup.object().shape({
  emails: Yup.array()
    .of(Yup.string().email("Invalid email format").required())
    .min(1, "Please select at least one email")
    .required("Email is required"),
  cc_emails: Yup.array()
    .of(Yup.string())
    .min(1, "Please select at least one CC email")
    .required("CC email is required"),
});

export const BulkWFormRequest = ({
  isOpen,
  close,
  groupId,
  merchantDetails,
  updateMerchant,
  season,
  handleSuccess,
}: BulkWFormRequestProps) => {
  const group = useCurrentGroupContext();
  const [bulkWFormRequest, { isLoading }] = useBulkWformRequestMutation();
  const { alertToast, successToast } = useToast();
  const { data: teamMembers, isLoading: isLoadingTeamMembers } =
    useGetTeamMembersQuery({ groupId }, { skip: !groupId });

  const requestSeason = season || CURRENT_SEASON;

  const allTeamEmails =
    teamMembers?.group_users
      ?.filter(
        ({ is_archived_user, is_verified }) => !is_archived_user && is_verified
      )
      ?.map(({ email }) => email) || [];

  const ccEmailOptions = allTeamEmails.map((email) => ({
    label: email,
    value: email,
  }));

  const onEmailSubmit = async (values: {
    emails: string[];
    cc_emails: string[];
  }) => {
    try {
      const merchant_details: MerchantDetail[] | null = values.emails
        .map((email) => {
          const detail = merchantDetails.find((d) => d.email === email);
          return detail ? { email, merchant_id: detail.merchant_id } : null;
        })
        .filter((d) => d !== null);

      const ccEmailsString =
        values.cc_emails.length > 0 ? values.cc_emails.join(",") : undefined;

      await bulkWFormRequest({
        groupId,
        payload: {
          merchant_details,
          season: requestSeason,
          cc_emails: ccEmailsString,
        },
      }).unwrap();

      updateMerchant();
      successToast({ message: "Email has been sent" });
      handleSuccess();
      close();
    } catch (error: any) {
      alertToast({ message: error?.response?.data?.error?.message });
    }
  };

  const options = merchantDetails.map(({ email }) => ({
    label: email,
    value: email,
  }));

  const initialEmails = merchantDetails.map((d) => d.email);

  return (
    <Modal.Root open={isOpen} onOpenChange={close}>
      <Modal.Portal>
        <Modal.Overlay />
        <Modal.Content size="large" asChild>
          <FormikForm
            onSubmit={onEmailSubmit}
            initialValues={{ emails: initialEmails, cc_emails: allTeamEmails }}
            validationSchema={validationSchema}
            enableReinitialize
          >
            {({ isSubmitting, values }) => {
              return (
                <>
                  <Modal.Header>
                    <Modal.Title>Request W-Form</Modal.Title>
                    <Modal.Close />
                  </Modal.Header>
                  <Modal.Body className="t-flex t-flex-col t-gap-4">
                    <p className="t-m-0 t-text-body-sm t-text-text-60">
                      Please enter the vendor's email address to request the
                      W-Form. An email will be sent to the vendor with the
                      following message.
                    </p>
                    <Combobox
                      withForm
                      name="emails"
                      label="To"
                      isMulti
                      options={options}
                      value={values.emails.map((email: string) => ({
                        label: email,
                        value: email,
                      }))}
                    />

                    <Combobox
                      name="cc_emails"
                      label="CC"
                      size="regular"
                      isMulti
                      withForm
                      placeholder="Add CC email addresses"
                      options={ccEmailOptions}
                      value={values.cc_emails.map((email: string) => ({
                        label: email,
                        value: email,
                      }))}
                    />

                    <div>
                      <div className="t-mb-3 t-flex t-items-center t-justify-center t-h-full t-gap-3">
                        <div className="t-w-full t-bg-purple-divider-gradient t-h-[1px] t-rotate-180" />
                        <div className="t-text-nowrap t-text-subtext-sm t-text-text-60">
                          Email Preview
                        </div>
                        <div className="t-w-full t-bg-purple-divider-gradient t-h-[1px]" />
                      </div>

                      <WFormEmailPreview
                        groupName={group.name}
                        season={requestSeason}
                      />
                    </div>
                  </Modal.Body>

                  <Modal.FooterButtonGroup>
                    <Modal.RawClose asChild>
                      <Button>Cancel</Button>
                    </Modal.RawClose>
                    <Button
                      customType="primary"
                      isLoading={
                        isSubmitting || isLoading || isLoadingTeamMembers
                      }
                      disabled={
                        isSubmitting || isLoading || isLoadingTeamMembers
                      }
                    >
                      Send email
                    </Button>
                  </Modal.FooterButtonGroup>
                </>
              );
            }}
          </FormikForm>
        </Modal.Content>
      </Modal.Portal>
    </Modal.Root>
  );
};
